/**
 * KapoeiraAngolaSite — fully editable recreation of casasamba.com/kapoeira-angola.
 * Every heading, paragraph, list item, price, button label, and image is wired
 * to PageEditModeContext via PageEditable / PageEditableImage.
 * Route: /kapoeira-angola. This is SEPARATE from /capoeira (Frog Style).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, MapPin, Phone, Mail, Star, Users,
  BookOpen, ArrowRight, Heart, Award, Shield, Crown
} from 'lucide-react';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditableImage } from '@/components/edit/PageEditableImage';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const WP = 'https://www.casasamba.com/wp-content/uploads/2022/03';
const WP2 = 'https://www.casasamba.com/wp-content/uploads/2022/02';

const IMG = {
  heroBackground: 'https://www.casasamba.com/wp-content/uploads/2026/09/ba7e1b7f8460ffe017b50ce5a3cccb869293ab78a9fe6e79f15792f81f04ee11.png',
  mestreOverlay: 'https://www.casasamba.com/wp-content/uploads/2026/09/ygwclug5_Mestre-Avatar-ID-photo-2.png',
  mestreCurtis: `${WP}/curtis_capoeira.jpg`,
  kids: `${WP}/6-1024x768.jpeg`,
  demoGif1: `${WP}/capoeira-2007-mini-clip-demo-high.gif`,
  demoGif2: `${WP}/capoeira-2007-mini-clip-demo-high-1.gif`,
  bookCover: `${WP}/capoeira.png`,
};

const BENEFITS = [
  { icon: Heart,    key: 'fitness',   title: 'Physical Fitness',    body: 'Full-body strength, cardio, and flexibility.' },
  { icon: Shield,   key: 'defense',   title: 'Self-Defense',        body: 'Learn to read, evade, and respond to attack.' },
  { icon: Users,    key: 'community', title: 'Community',           body: 'Join a global family of Angoleiros.' },
  { icon: Star,     key: 'focus',     title: 'Discipline & Focus',  body: 'The roda teaches presence and patience.' },
  { icon: Award,    key: 'heritage',  title: 'Cultural Heritage',   body: 'Speak Portuguese, sing, play the berimbau.' },
  { icon: BookOpen, key: 'philosophy',title: 'Philosophy',          body: 'Malícia, mandinga, respect for elders.' },
];

const SCHEDULE = [
  { key: 'wed', day: 'Wednesday', time: '6:00 PM',       type: 'All Levels' },
  { key: 'fri', day: 'Friday',    time: '6:00 PM',       type: 'All Levels' },
  { key: 'sun', day: 'Sunday',    time: '10 AM – 12 PM', type: 'Private Appointments' },
];

const KapoeiraAngolaSiteInner = () => (
  <div className="min-h-screen bg-stone-950 text-white" data-testid="kapoeira-angola-site">

    {/* HERO */}
    <section className="relative overflow-hidden border-b border-amber-500/20 bg-black">
      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="relative mb-12"
        >
          <div className="absolute inset-0 bg-amber-500/5 blur-3xl rounded-full" />
          <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-xl shadow-amber-900/30">
            <PageEditableImage
              path="hero.backgroundImage"
              fallback={IMG.heroBackground}
              alt="Kapoeira Angola background"
              className="w-full"
            />
            <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
              <PageEditableImage
                path="hero.mestreImage"
                fallback={IMG.mestreOverlay}
                alt="Mestre Curtis Pierre"
                className="h-[160%] w-auto max-w-[85%] object-contain object-bottom"
              />
            </div>
            <div className="absolute inset-0 bg-black/55 pointer-events-none" />
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black px-6 py-2 rounded-full font-black text-sm flex items-center gap-2 shadow-lg z-10"
            >
              <Crown className="w-4 h-4" />
              <PageEditable path="hero.badge" fallback="KAPOEIRA ANGOLA" />
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
            <Crown className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 uppercase tracking-[0.2em] text-xs font-bold">
              <PageEditable path="hero.eyebrow" fallback="Casa Samba presents" />
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-4 leading-tight text-white">
            <PageEditable path="hero.title" fallback="Kapoeira" />{' '}
            <span className="text-amber-400">
              <PageEditable path="hero.titleAccent" fallback="Angola" />
            </span>
          </h1>
          <PageEditable
            path="hero.subtitle"
            as="p"
            multiline
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto italic"
            fallback="An Afro-Brasilian martial art of self-defense, dance, and sacred acrobatics — practiced in New Orleans by Grupo Besouro under Mestre Curtis Pierre."
          />
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#schedule" className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl transition-colors inline-flex items-center gap-2" data-testid="hero-schedule-btn">
              <Calendar className="w-4 h-4" />
              <PageEditable path="hero.ctaPrimary" fallback="Class Schedule" />
            </a>
            <a href="#contact" className="px-8 py-3 border border-amber-500/40 text-amber-400 font-bold rounded-xl hover:bg-amber-500/10 transition-colors">
              <PageEditable path="hero.ctaSecondary" fallback="1st Class FREE" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>

    {/* JUMPSTART */}
    <section className="py-16 px-6 bg-stone-900/50 border-b border-amber-500/10">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-l-4 border-amber-500 rounded-r-xl py-6 px-8">
          <h2 className="text-2xl md:text-3xl font-black text-amber-400 mb-3 tracking-wide">
            <PageEditable path="jumpstart.heading" fallback="JUMPSTART YOUR HEALTH!" />
          </h2>
          <PageEditable
            path="jumpstart.body"
            as="p"
            multiline
            className="text-gray-300 leading-relaxed"
            fallback="Kapoeira is a full-body workout that builds strength, flexibility, rhythm, and confidence — while connecting you to a 400-year-old African tradition. Your very first class is FREE. Come in your comfortable clothes, no experience needed."
          />
        </div>
      </div>
    </section>

    {/* WHAT IS KAPOEIRA ANGOLA */}
    <section className="py-20 px-6 bg-stone-950">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
            <PageEditable path="about.eyebrow" fallback="The Tradition" />
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            <PageEditable path="about.titlePre" fallback="What is Kapoeira " />
            <span className="text-amber-400"><PageEditable path="about.titleAccent" fallback="Angola?" /></span>
          </h2>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            <PageEditable
              path="about.p1"
              as="p"
              multiline
              fallback="Kapoeira Angola is the oldest and most traditional form of Kapoeira. Brought to Brasil by enslaved Africans from Angola, Congo, and other Central African kingdoms, it disguised self-defense inside dance, song, and ritual so that Africans could keep their fighting arts alive under colonial law."
            />
            <PageEditable
              path="about.p2"
              as="p"
              multiline
              fallback="Practiced in the roda — a circle of players, singers, and musicians — Kapoeira Angola emphasizes cunning, low movements, deception, and deep respect for one's elders and lineage. It is played slow. It is played close. It is played with the whole body listening to the berimbau."
            />
            <PageEditable
              path="about.p3"
              as="p"
              multiline
              fallback="In New Orleans, Grupo Besouro under Mestre Curtis Pierre carries this tradition forward — training children, adults, and elders in the movement, music, and philosophy of one of the world's most beautiful martial arts."
            />
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-3 bg-amber-500/20 rounded-2xl blur-2xl" />
          <PageEditableImage
            path="about.image"
            fallback={IMG.mestreCurtis}
            alt="Mestre Curtis Pierre"
            className="relative rounded-2xl border border-amber-500/20 shadow-2xl w-full"
          />
          <div className="absolute -bottom-4 -right-4 bg-amber-500 text-black px-5 py-3 rounded-xl shadow-2xl">
            <div className="text-xl font-black">
              <PageEditable path="about.badgeTitle" fallback="Mestre Curtis" />
            </div>
            <div className="text-black/70 text-sm">
              <PageEditable path="about.badgeSubtitle" fallback="Grupo Besouro" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* LIVE MOVEMENT DEMO */}
    <section className="py-16 px-6 bg-gradient-to-b from-stone-950 to-black">
      <div className="max-w-6xl mx-auto text-center mb-10">
        <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
          <PageEditable path="demo.eyebrow" fallback="See It In Motion" />
        </p>
        <h2 className="text-4xl font-black mb-3">
          <PageEditable path="demo.titlePre" fallback="Kapoeira Angola, " />
          <span className="text-amber-400"><PageEditable path="demo.titleAccent" fallback="Live" /></span>
        </h2>
        <PageEditable
          path="demo.subtitle"
          as="p"
          multiline
          className="text-gray-400 max-w-2xl mx-auto"
          fallback="Movement demonstrations recorded during New Orleans Kapoeira classes."
        />
      </div>
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl overflow-hidden border border-amber-500/30 aspect-video bg-black">
          <PageEditableImage path="demo.clip1" fallback={IMG.demoGif1} alt="Kapoeira demonstration 1" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-2xl overflow-hidden border border-amber-500/30 aspect-video bg-black">
          <PageEditableImage path="demo.clip2" fallback={IMG.demoGif2} alt="Kapoeira demonstration 2" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>

    {/* BENEFITS */}
    <section className="py-20 px-6 bg-stone-950">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
            <PageEditable path="benefits.eyebrow" fallback="What You Will Gain" />
          </p>
          <h2 className="text-4xl md:text-5xl font-black">
            <PageEditable path="benefits.titlePre" fallback="Acquired " />
            <span className="text-amber-400"><PageEditable path="benefits.titleAccent" fallback="Benefits" /></span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="p-6 rounded-2xl bg-stone-900/60 border border-amber-500/10 hover:border-amber-500/40 transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center mb-3">
                <b.icon className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                <PageEditable path={`benefits.${b.key}.title`} fallback={b.title} />
              </h3>
              <PageEditable
                path={`benefits.${b.key}.body`}
                as="p"
                multiline
                className="text-gray-400 text-sm"
                fallback={b.body}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* KIDS */}
    <section className="py-20 px-6 bg-gradient-to-b from-stone-950 to-black">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div className="relative order-2 md:order-1">
          <div className="absolute -inset-3 bg-amber-500/20 rounded-2xl blur-2xl" />
          <PageEditableImage
            path="kids.image"
            fallback={IMG.kids}
            alt="Kapoeira Kids Practice"
            className="relative rounded-2xl border border-amber-500/20 shadow-2xl w-full"
          />
        </div>
        <div className="order-1 md:order-2">
          <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
            <PageEditable path="kids.eyebrow" fallback="For the Next Generation" />
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            <PageEditable path="kids.titlePre" fallback="Kapoeira " />
            <span className="text-amber-400"><PageEditable path="kids.titleAccent" fallback="Kids Practice" /></span>
          </h2>
          <PageEditable
            path="kids.body"
            as="p"
            multiline
            className="text-gray-300 leading-relaxed mb-4"
            fallback="Children learn Kapoeira starting at the age of 5. It builds strength, coordination, focus, and confidence — while teaching the rhythm, music, and philosophy of an ancient African art form."
          />
          <ul className="space-y-3 mb-6">
            {['bullet1','bullet2','bullet3'].map((k, i) => (
              <li key={k} className="flex gap-3 text-gray-300">
                <Star className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <PageEditable
                  path={`kids.${k}`}
                  fallback={[
                    'Age-appropriate movement, music, and games — never aggressive',
                    'Teaches respect, responsibility, and fairness through the roda',
                    'FREE for all children under 18',
                  ][i]}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* SCHEDULE */}
    <section id="schedule" className="py-20 px-6 bg-stone-900/70 scroll-mt-24 border-y border-amber-500/10">
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
          <PageEditable path="schedule.eyebrow" fallback="When to Train" />
        </p>
        <h2 className="text-4xl md:text-5xl font-black mb-4">
          <PageEditable path="schedule.titlePre" fallback="Class " />
          <span className="text-amber-400"><PageEditable path="schedule.titleAccent" fallback="Schedule" /></span>
        </h2>
        <PageEditable
          path="schedule.subtitle"
          as="p"
          multiline
          className="text-gray-400 mb-10"
          fallback="All classes at Escola de Samba Casa Samba, 800 Race Street, New Orleans. Your first class is FREE."
        />
        <div className="grid md:grid-cols-3 gap-4">
          {SCHEDULE.map((c) => (
            <div key={c.key} className="bg-stone-950 border border-amber-500/20 hover:border-amber-500/60 transition-colors rounded-2xl p-6" data-testid={`ka-schedule-${c.day.toLowerCase()}`}>
              <Calendar className="w-6 h-6 text-amber-400 mx-auto mb-3" />
              <div className="text-2xl font-black text-white">
                <PageEditable path={`schedule.${c.key}.day`} fallback={c.day} />
              </div>
              <div className="text-lg text-amber-400 font-bold mt-1">
                <PageEditable path={`schedule.${c.key}.time`} fallback={c.time} />
              </div>
              <div className="text-sm text-gray-500 mt-2">
                <PageEditable path={`schedule.${c.key}.type`} fallback={c.type} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 inline-flex items-center gap-2 text-gray-400 text-sm">
          <MapPin className="w-4 h-4 text-amber-400" />
          <PageEditable path="schedule.address" fallback="800 Race Street, New Orleans, LA 70130" />
        </div>
      </div>
    </section>

    {/* STUDY GUIDE */}
    <section className="py-20 px-6 bg-stone-950">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
            <PageEditable path="book.eyebrow" fallback="Publication" />
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            <PageEditable path="book.titlePre" fallback="A Kapoeira " />
            <span className="text-amber-400"><PageEditable path="book.titleAccent" fallback="Study Guide" /></span>
          </h2>
          <PageEditable
            path="book.body"
            as="p"
            multiline
            className="text-gray-300 leading-relaxed mb-6"
            fallback="A 324-page study guide by Mestre Curtis Pierre covering movement, music, history, and philosophy — the reference book for serious students of Kapoeira Angola in New Orleans and beyond."
          />
          <Link to="/kapoeira-book" className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl transition-colors" data-testid="ka-book-cta">
            <BookOpen className="w-4 h-4" />
            <PageEditable path="book.cta" fallback="Read the Book" />
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="relative">
          <div className="absolute -inset-3 bg-amber-500/20 rounded-2xl blur-2xl" />
          <PageEditableImage
            path="book.cover"
            fallback={IMG.bookCover}
            alt="Kapoeira Angola study guide"
            className="relative rounded-2xl border border-amber-500/20 shadow-2xl w-full max-w-md mx-auto"
          />
        </div>
      </div>
    </section>

    {/* CONTACT */}
    <section id="contact" className="py-20 px-6 bg-gradient-to-b from-black to-stone-950 scroll-mt-24 border-t border-amber-500/10">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-amber-500 uppercase tracking-widest text-xs font-bold mb-3">
          <PageEditable path="contact.eyebrow" fallback="Get In Touch" />
        </p>
        <h2 className="text-4xl md:text-5xl font-black mb-10">
          <PageEditable path="contact.titlePre" fallback="Come " />
          <span className="text-amber-400"><PageEditable path="contact.titleAccent" fallback="Train With Us" /></span>
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="p-6 bg-stone-900/60 border border-amber-500/10 rounded-2xl hover:border-amber-500/40 transition-colors">
            <MapPin className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <h3 className="font-bold text-white mb-1">
              <PageEditable path="contact.locationLabel" fallback="Location" />
            </h3>
            <PageEditable
              path="contact.locationValue"
              as="p"
              multiline
              className="text-gray-400 text-sm"
              fallback="800 Race Street, New Orleans, LA 70130"
            />
          </div>
          <div className="p-6 bg-stone-900/60 border border-amber-500/10 rounded-2xl hover:border-amber-500/40 transition-colors">
            <Phone className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <h3 className="font-bold text-white mb-1">
              <PageEditable path="contact.phoneLabel" fallback="Phone" />
            </h3>
            <p className="text-amber-400 font-bold">
              <PageEditable path="contact.phoneValue" fallback="504-236-7479" />
            </p>
          </div>
          <div className="p-6 bg-stone-900/60 border border-amber-500/10 rounded-2xl hover:border-amber-500/40 transition-colors">
            <Mail className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <h3 className="font-bold text-white mb-1">
              <PageEditable path="contact.emailLabel" fallback="Email" />
            </h3>
            <p className="text-amber-400 font-bold text-sm">
              <PageEditable path="contact.emailValue" fallback="casasambacurtis@gmail.com" />
            </p>
          </div>
        </div>
        <a href="#schedule" className="inline-flex items-center gap-2 px-10 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl transition-colors text-lg" data-testid="ka-cta-schedule-jump">
          <Calendar className="w-5 h-5" />
          <PageEditable path="contact.ctaBottom" fallback="See Class Schedule" />
        </a>
      </div>
    </section>

    <footer className="py-8 px-6 bg-black text-gray-500 text-center text-sm border-t border-amber-500/10">
      <PageEditable
        path="footer.text"
        fallback="© Casa Samba Inc. · Kapoeira Angola · Grupo Besouro · New Orleans"
      />
    </footer>
  </div>
);

const KapoeiraAngolaSite = () => (
  <PageEditProvider slug="kapoeira-angola">
    <KapoeiraAngolaSiteInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/dashboard#kapoeira-editor-btn" label="Edit Kapoeira Angola" />
  </PageEditProvider>
);

export default KapoeiraAngolaSite;
